import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  CATALOG,
  EntrySignalGroup,
  entrySignalTooltip,
  GATES,
  LIBRARY_GROUPS,
  RZ_CHANNELS,
  RZ_TEMPLATES,
  S09Branch,
  S09DesignDoc,
  S09Link,
  S09ModuleStep,
  S09OutreachMod,
  S09Side,
  S09Step,
  TRIGGERS,
  CaseStage,
  CaseTypeOption,
  ReasonCodeOption,
  TriggerConditionOption,
  blankStepValues,
  catalogEntry,
  defaultMod,
  isS09Branch,
  nextModuleStepId,
  triggerMeta,
} from "./s09-workflow.models";
import { addAttemptSteps, addLibraryStep, ensureCanvasLayout, ensureLinks, ensureModuleInnerSteps, insertStepBetween, nextStepId } from "./s09-workflow-mapper";
import { WorkflowService } from "../../service/workflow.service";

type InspMode = "none" | "trigger" | "step" | "attempt";
type DragTarget = "step" | "trigger";

interface CanvasDrag {
  target: DragTarget;
  id: string | null;
  startX: number;
  startY: number;
  origX: number;
  origY: number;
  moved: boolean;
}

interface CanvasResize {
  id: string;
  startX: number;
  startY: number;
  origW: number;
  origH: number;
}

export interface S09Finding {
  level: "INFO" | "WARNING" | "ERROR" | "PASS";
  where: string;
  message: string;
  colour: string;
}

@Component({
  selector: "app-s09-workflow-designer",
  templateUrl: "./s09-workflow-designer.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
  host: { class: "s09-designer-host" },
})
export class S09WorkflowDesignerComponent implements OnInit, OnChanges {
  @Input() doc!: S09DesignDoc;
  @Input() wfName = "";
  @Input() status = "DRAFT";
  @Input() version = 1;
  @Input() brandKey: number | null = null;
  @Input() toast = "";
  @Input() saving = false;
  /** When true, name/version/status live in the shell header (edit mode). */
  @Input() hideNameBar = false;

  @Output() docChange = new EventEmitter<S09DesignDoc>();
  @Output() nameChange = new EventEmitter<string>();
  @Output() back = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();
  @Output() activate = new EventEmitter<void>();
  @Output() pause = new EventEmitter<void>();

  libOpen = false;
  valOpen = false;
  libQuery = "";
  library: { group: string; items: any[] }[] = [];
  libraryCount = 0;
  /** When set, the step library inserts into the open outreach module at this index. */
  moduleInsertAt: number | null = null;
  /** When set, the step library inserts into this attempt's after-gap. */
  afterInsert: { attempt: number; at: number } | null = null;
  /** When set, the step library inserts a step onto this canvas connection. */
  linkInsert: { from: string; to: string } | null = null;
  linkPick = "";
  /** Drag from an output port to attach, or from an arrow to move or detach. */
  linkGesture: {
    from: string;
    movingTo: string | null;
    branch: S09Branch | null;
    x: number;
    y: number;
    ox: number;
    oy: number;
    over: string | null;
    side: S09Side | null;
    fromSide: "left" | "right" | null;
    moved: boolean;
  } | null = null;
  /** Drag the elbow so the line routes around other steps. */
  bendGesture: {
    from: string;
    to: string;
    branch: S09Branch | null;
    ox: number;
    oy: number;
    moved: boolean;
  } | null = null;
  wireHover: string | null = null;
  wireSelected: string | null = null;
  selectedId: string | null = null;
  selectedAttempt: number | null = null;
  selectedModStepId: string | null = null;
  inspMode: InspMode = "none";
  gateRefused = false;

  readonly triggers = TRIGGERS as any[];
  readonly channels = RZ_CHANNELS as string[];
  /** Loaded from API; falls back to static RZ_TEMPLATES when empty. */
  templates: any[] = [...(RZ_TEMPLATES as any[])];
  readonly gates = GATES;

  trackGroup = (_: number, g: { group: string }) => g.group;
  trackLibItem = (_: number, it: { kind: string }) => it.kind;
  trackStep = (_: number, s: S09Step) => s.id;
  trackWire = (_: number, w: { key: string }) => w.key;
  trackFinding = (_: number, f: S09Finding) => f.level + f.where + f.message;

  signalGroups: EntrySignalGroup[] = [];
  reasonCodes: ReasonCodeOption[] = [];
  statusCodeFilter = "";
  readonly conditionOps = ["Equal", "Not equal", "In", "Not In", "Greater than", "Less than"];
  signalsHint = "";
  caseTypes: CaseTypeOption[] = [];
  caseStages: CaseStage[] = [];
  private loadedStageKey: number | null | undefined = undefined;
  allConditions: TriggerConditionOption[] = [];
  conditionsHint = "";

  private drag: CanvasDrag | null = null;
  private resize: CanvasResize | null = null;
  private suppressClick = false;
  /** Snapshot taken at drag/resize start for a single undo entry. */
  private gestureSnapshot: S09DesignDoc | null = null;
  private undoStack: S09DesignDoc[] = [];
  private redoStack: S09DesignDoc[] = [];
  private readonly historyLimit = 40;
  /** Last copied canvas step. Paste creates a new step from this copy. */
  private stepClipboard: S09Step | null = null;
  private pasteNudge = 0;

  constructor(
    private workflowService: WorkflowService,
    private host: ElementRef<HTMLElement>
  ) {}

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  get canDeleteStep(): boolean {
    return !!this.selected && this.inspMode !== "trigger";
  }

  get canCopyStep(): boolean {
    return this.canDeleteStep;
  }

  get canPasteStep(): boolean {
    return !!this.stepClipboard;
  }

  ngOnInit(): void {
    this.rebuildLibrary();
    this.loadLookups();
    this.ensureLayout();
  }

  get conditionsForTrigger(): TriggerConditionOption[] {
    const t = (this.doc?.trigger || "").toLowerCase();
    return this.allConditions.filter((c) => c.trigger_type?.toLowerCase() === t);
  }

  attCols(n: number): string {
    const c = Math.min(Math.max(n || 1, 1), 3);
    return `repeat(${c}, minmax(0, 1fr))`;
  }

  setCodesCsv(raw: string): void {
    const codes = String(raw || "")
      .split(/[,·;]/)
      .map((x) => x.trim())
      .filter(Boolean);
    this.patchMeta("codes", codes);
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch["doc"]) {
      if (!this.library.length) this.rebuildLibrary();
      this.syncCaseStages();
      // Skip while dragging/resizing so parent rebinds don't fight the gesture.
      if (!this.drag && !this.resize) this.ensureLayout();
    }
    if (ch["brandKey"] && !ch["brandKey"].firstChange) this.loadLookups();
  }

  private ensureLayout(): void {
    if (!this.doc) return;
    let next = ensureModuleInnerSteps(this.doc);
    const needs =
      !next.layout?.trigger ||
      next.steps.some((s) => s.x == null || s.y == null || s.w == null);
    if (needs) next = ensureCanvasLayout(next);
    const linked = ensureLinks(next);
    if (linked !== next) next = linked;
    if (next !== this.doc) this.emit(next, { history: false });
  }

  private cloneDoc(doc: S09DesignDoc): S09DesignDoc {
    return JSON.parse(JSON.stringify(doc)) as S09DesignDoc;
  }

  private pushHistory(snapshot: S09DesignDoc): void {
    this.undoStack.push(this.cloneDoc(snapshot));
    if (this.undoStack.length > this.historyLimit) this.undoStack.shift();
    this.redoStack = [];
  }

  get triggerGeom() {
    return this.doc?.layout?.trigger || { x: 120, y: 28, w: 240 };
  }

  get worldW(): number {
    let max = 900;
    const t = this.triggerGeom;
    max = Math.max(max, (t.x || 0) + (t.w || 240) + 80);
    (this.doc?.steps || []).forEach((s) => {
      max = Math.max(max, (s.x || 0) + (s.w || 220) + 80);
    });
    return max;
  }

  get worldH(): number {
    let max = 700;
    const t = this.triggerGeom;
    max = Math.max(max, (t.y || 0) + 80);
    (this.doc?.steps || []).forEach((s) => {
      max = Math.max(max, (s.y || 0) + (s.h || 110) + 80);
    });
    return max;
  }

  wires(): {
    key: string;
    d: string;
    from: string;
    to: string;
    branch: S09Branch | null;
    hx: number;
    hy: number;
    ex: number;
    ey: number;
    nx: number;
    ny: number;
  }[] {
    if (!this.doc) return [];
    const moving = this.linkGesture?.movingTo;
    const movingBranch = this.linkGesture?.branch || null;
    return (this.doc.links || [])
      .filter(
        (link) =>
          !(
            moving &&
            link.from === this.linkGesture?.from &&
            link.to === moving &&
            (link.branch || null) === movingBranch
          )
      )
      .map((link) => {
        const branch = isS09Branch(link.branch) ? link.branch : null;
        const side = link.toSide === "left" || link.toSide === "right" ? link.toSide : "top";
        const depart = link.fromSide === "left" || link.fromSide === "right" ? link.fromSide : "bottom";
        const startEnd = branch || (depart === "bottom" ? "out" : `out-${depart}`);
        const a = this.portCenter(link.from, startEnd);
        const b = this.inPoint(link.to, side);
        const route = this.route(a.x, a.y, b.x, b.y, side, link.bendX, link.bendY, branch ? "bottom" : depart);
        return {
          key: `${link.from}>${link.to}>${branch || ""}`,
          d: route.d,
          from: link.from,
          to: link.to,
          branch,
          hx: route.hx,
          hy: route.hy,
          ex: b.x,
          ey: b.y,
          nx: -7,
          ny: -7,
        };
      });
  }

  rubberPath(): string {
    const g = this.linkGesture;
    if (!g) return "";
    const depart = g.fromSide === "left" || g.fromSide === "right" ? g.fromSide : "bottom";
    const startEnd = isS09Branch(g.branch) ? g.branch : depart === "bottom" ? "out" : `out-${depart}`;
    const a = this.portCenter(g.from, startEnd);
    let x2 = g.x;
    let y2 = g.y;
    let side: S09Side = "top";
    if (g.over && g.side) {
      const at = this.inPoint(g.over, g.side);
      x2 = at.x;
      y2 = at.y;
      side = g.side;
    }
    return this.route(a.x, a.y, x2, y2, side, undefined, undefined, g.branch ? "bottom" : depart).d;
  }

  /** Orthogonal connector. A saved bend pulls the line off the straight path. Side arrivals enter from that edge. */
  private route(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    side: S09Side = "top",
    bendX?: number,
    bendY?: number,
    depart: "bottom" | "left" | "right" = "bottom"
  ): { d: string; hx: number; hy: number } {
    const x1r = Math.round(x1);
    const y1r = Math.round(y1);
    const x2r = Math.round(x2);
    const y2r = Math.round(y2);
    if (depart === "left" || depart === "right") {
      const stub = depart === "right" ? x1r + 28 : x1r - 28;
      const rest = this.route(stub, y1r, x2r, y2r, side, bendX, bendY, "bottom");
      const tail = rest.d.replace(/^M\s+[-\d.]+\s+[-\d.]+\s*/, "");
      return {
        d: `M ${x1r} ${y1r} L ${stub} ${y1r} ${tail}`,
        hx: rest.hx,
        hy: rest.hy,
      };
    }
    if (side === "left" || side === "right") {
      const approach = side === "left" ? x2r - 18 : x2r + 18;
      const lane = Math.round(bendX != null ? bendX : approach);
      const yTurn = Math.round(
        bendY != null ? bendY : y2r >= y1r + 16 ? (y1r + y2r) / 2 : y1r + 26
      );
      return {
        d: `M ${x1r} ${y1r} L ${x1r} ${yTurn} L ${lane} ${yTurn} L ${lane} ${y2r} L ${x2r} ${y2r}`,
        hx: Math.round((x1r + lane) / 2),
        hy: yTurn,
      };
    }
    if (y2r >= y1r + 20 && bendX == null && bendY == null && Math.abs(x1r - x2r) < 10) {
      return { d: `M ${x1r} ${y1r} L ${x2r} ${y2r}`, hx: x1r, hy: Math.round((y1r + y2r) / 2) };
    }
    if (y2r >= y1r + 20) {
      const mid =
        bendY != null ? Math.round(bendY) : Math.round(y1r + Math.max(18, (y2r - y1r) / 2));
      const lane = bendX != null ? Math.round(bendX) : null;
      if (lane == null || (Math.abs(lane - x1r) < 8 && Math.abs(lane - x2r) < 8)) {
        return {
          d: `M ${x1r} ${y1r} L ${x1r} ${mid} L ${x2r} ${mid} L ${x2r} ${y2r}`,
          hx: Math.round((x1r + x2r) / 2),
          hy: mid,
        };
      }
      const yIn = y2r - 14;
      return {
        d: `M ${x1r} ${y1r} L ${x1r} ${mid} L ${lane} ${mid} L ${lane} ${yIn} L ${x2r} ${yIn} L ${x2r} ${y2r}`,
        hx: lane,
        hy: mid,
      };
    }
    const lane = Math.round(bendX != null ? bendX : Math.max(x1r, x2r) + 64);
    const yOut = y1r + 26;
    const yIn = bendY != null ? Math.round(bendY) : y2r - 18;
    return {
      d: `M ${x1r} ${y1r} L ${x1r} ${yOut} L ${lane} ${yOut} L ${lane} ${yIn} L ${x2r} ${yIn} L ${x2r} ${y2r}`,
      hx: lane,
      hy: yIn,
    };
  }

  private portCenter(id: string, end: string): { x: number; y: number } {
    const world = this.host.nativeElement.querySelector(".s09-canvas-world") as HTMLElement | null;
    const port = this.host.nativeElement.querySelector(
      `[data-port="${id}-${end}"]`
    ) as HTMLElement | null;
    if (world && port) {
      const wr = world.getBoundingClientRect();
      const pr = port.getBoundingClientRect();
      if (pr.width) {
        return {
          x: pr.left + pr.width / 2 - wr.left,
          y: pr.top + pr.height / 2 - wr.top,
        };
      }
    }
    return this.linkPoint(id, end);
  }

  private toWorld(ev: PointerEvent): { x: number; y: number } {
    const world = this.host.nativeElement.querySelector(".s09-canvas-world") as HTMLElement | null;
    if (!world) return { x: 0, y: 0 };
    const wr = world.getBoundingClientRect();
    return { x: ev.clientX - wr.left, y: ev.clientY - wr.top };
  }

  private inPoint(id: string, side: S09Side): { x: number; y: number } {
    const box = this.stepBox(id);
    if (box) {
      const midY = box.top + (box.bottom - box.top) / 2;
      if (side === "left") return { x: box.left, y: midY };
      if (side === "right") return { x: box.right, y: midY };
      return { x: (box.left + box.right) / 2, y: box.top };
    }
    const end = side === "left" ? "in-left" : side === "right" ? "in-right" : "in";
    return this.portCenter(id, end);
  }

  private stepBox(id: string): { left: number; top: number; right: number; bottom: number } | null {
    const world = this.host.nativeElement.querySelector(".s09-canvas-world") as HTMLElement | null;
    const el = this.host.nativeElement.querySelector(`[data-step="${id}"]`) as HTMLElement | null;
    if (!world || !el) return null;
    const wr = world.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return {
      left: r.left - wr.left,
      top: r.top - wr.top,
      right: r.right - wr.left,
      bottom: r.bottom - wr.top,
    };
  }

  private nearestInPort(x: number, y: number, from: string): { id: string; side: S09Side } | null {
    let best: { id: string; side: S09Side } | null = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (const step of this.doc?.steps || []) {
      if (step.id === from) continue;
      const box = this.stepBox(step.id);
      if (!box) continue;
      const pad = 72;
      if (x < box.left - pad || x > box.right + pad || y < box.top - pad || y > box.bottom + pad) continue;
      const side = this.attachSide(x, y, box.left, box.top, box.right, box.bottom);
      const anchor =
        side === "left"
          ? { x: box.left, y: (box.top + box.bottom) / 2 }
          : side === "right"
            ? { x: box.right, y: (box.top + box.bottom) / 2 }
            : { x: (box.left + box.right) / 2, y: box.top };
      const d = Math.hypot(x - anchor.x, y - anchor.y);
      if (d < bestD) {
        bestD = d;
        best = { id: step.id, side };
      }
    }
    return best;
  }

  /** Left or right when the pointer is nearer that edge than the top. */
  private attachSide(x: number, y: number, left: number, top: number, right: number, bottom: number): S09Side {
    const distLeft = Math.abs(x - left);
    const distRight = Math.abs(x - right);
    const distTop = Math.abs(y - top);
    const distBottom = Math.abs(y - bottom);
    const sideDist = Math.min(distLeft, distRight);
    if (sideDist < distTop && sideDist <= distBottom) return distLeft <= distRight ? "left" : "right";
    return "top";
  }

  startLink(
    ev: PointerEvent,
    from: string,
    movingTo: string | null,
    branch: S09Branch | null = null,
    fromSide: "left" | "right" | null = null
  ): void {
    ev.preventDefault();
    ev.stopPropagation();
    const at = this.toWorld(ev);
    const existing = (this.doc?.links || []).find((l) => l.from === from && l.to === movingTo);
    const keptSide = existing?.fromSide === "left" || existing?.fromSide === "right" ? existing.fromSide : null;
    this.linkGesture = {
      from,
      movingTo,
      branch: branch || existing?.branch || null,
      x: at.x,
      y: at.y,
      ox: at.x,
      oy: at.y,
      over: null,
      side: null,
      fromSide: fromSide || keptSide,
      moved: false,
    };
    (ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
  }

  private finishLink(): void {
    const g = this.linkGesture;
    this.linkGesture = null;
    if (!g || !this.doc || !g.moved) return;
    let links = [...(this.doc.links || [])];
    if (g.movingTo) {
      links = links.filter((l) => !(l.from === g.from && l.to === g.movingTo));
    }
    if (g.branch) {
      links = links.filter((l) => !(l.from === g.from && l.branch === g.branch));
    }
    if (g.over && g.over !== g.from) {
      const side = g.side === "left" || g.side === "right" ? g.side : undefined;
      const fromSide = g.fromSide === "left" || g.fromSide === "right" ? g.fromSide : undefined;
      const previous = (this.doc.links || []).find(
        (l) => l.from === g.from && (g.movingTo ? l.to === g.movingTo : l.branch === g.branch)
      );
      const next: S09Link = { from: g.from, to: g.over };
      if (g.branch) next.branch = g.branch;
      if (side) next.toSide = side;
      if (fromSide && !g.branch) next.fromSide = fromSide;
      const sameSide = (previous?.toSide || "top") === (side || "top");
      if (previous && previous.to === g.over && sameSide) {
        if (typeof previous.bendX === "number") next.bendX = previous.bendX;
        if (typeof previous.bendY === "number") next.bendY = previous.bendY;
      }
      if (!links.some((l) => l.from === next.from && l.to === next.to && (l.branch || "") === (next.branch || ""))) {
        links.push(next);
      }
      this.emit({ ...this.doc, links });
      return;
    }
    if (g.movingTo) this.emit({ ...this.doc, links });
  }

  private linkPoint(id: string, end: string): { x: number; y: number } {
    if (id === "trigger") {
      const t = this.triggerGeom;
      return { x: (t.x || 0) + (t.w || 240) / 2, y: (t.y || 0) + 42 };
    }
    const step = (this.doc?.steps || []).find((s) => s.id === id);
    const w = step?.w || 220;
    const h = step?.h || 96;
    const x0 = step?.x || 0;
    const y0 = step?.y || 0;
    if (end === "in-left" || end === "out-left") return { x: x0, y: y0 + h / 2 };
    if (end === "in-right" || end === "out-right") return { x: x0 + w, y: y0 + h / 2 };
    const y = end === "in" ? y0 : y0 + h;
    const x =
      end === "false" || end === "escalate"
        ? x0 + w * 0.28
        : end === "true" || end === "wait"
          ? x0 + w * 0.72
          : x0 + w / 2;
    return { x, y };
  }

  outgoingOf(from: string): string[] {
    return (this.doc?.links || []).filter((l) => l.from === from).map((l) => l.to);
  }

  linkTargets(from: string): S09Step[] {
    const taken = new Set(this.outgoingOf(from));
    return (this.doc?.steps || []).filter((s) => s.id !== from && !taken.has(s.id));
  }

  connectFrom(): string {
    return this.inspMode === "trigger" ? "trigger" : this.selected?.id || "";
  }

  branchTarget(from: string, branch: S09Branch): string {
    return (this.doc?.links || []).find((l) => l.from === from && l.branch === branch)?.to || "";
  }

  setBranch(from: string, branch: S09Branch, to: string): void {
    if (!this.doc || !from) return;
    let links = (this.doc.links || []).filter((l) => !(l.from === from && l.branch === branch));
    if (to) links = [...links, { from, to, branch }];
    this.emit({ ...this.doc, links });
  }

  conditionAttrs(): string[] {
    const left = (catalogEntry("condition")?.fields || []).find((f: any) => f.key === "left");
    return left?.options || [];
  }

  setConditionAttr(attr: string): void {
    const op = String(this.selected?.values?.["op"] || "");
    const patch: Record<string, any> = { left: attr, right: "" };
    if (!this.conditionOps.includes(op)) patch["op"] = "Equal";
    this.patchStepValues(patch);
  }

  setConditionOp(op: string): void {
    const patch: Record<string, any> = { op };
    if (
      this.selected?.values?.["left"] === "Latest status code" &&
      (op === "Equal" || op === "Not equal")
    ) {
      patch["right"] = this.conditionCodesSelected()[0] || "";
    }
    this.patchStepValues(patch);
  }

  conditionCodesSelected(): string[] {
    return String(this.selected?.values?.["right"] || "")
      .split(/[,·]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  isConditionCodeOn(code: string): boolean {
    const want = code.toUpperCase();
    return this.conditionCodesSelected().some((c) => c.toUpperCase() === want);
  }

  toggleConditionCode(code: string): void {
    const op = String(this.selected?.values?.["op"] || "Equal");
    if (op !== "In" && op !== "Not In") {
      this.patchStepValue("right", this.isConditionCodeOn(code) ? "" : code);
      return;
    }
    const current = this.conditionCodesSelected().filter(
      (c) => c.toUpperCase() !== code.toUpperCase()
    );
    if (!this.isConditionCodeOn(code)) current.push(code);
    this.patchStepValue("right", current.join(", "));
  }

  conditionCodeChoices(): ReasonCodeOption[] {
    const rows = this.reasonCodes.length ? this.reasonCodes : this.codesFromSignals();
    const q = this.statusCodeFilter.trim().toLowerCase();
    const matched = q
      ? rows.filter((r) => `${r.reason_code} ${r.reason_desc || ""}`.toLowerCase().includes(q))
      : rows;
    return matched.slice(0, 48);
  }

  private codesFromSignals(): ReasonCodeOption[] {
    const seen = new Set<string>();
    const out: ReasonCodeOption[] = [];
    for (const group of this.signalGroups) {
      for (const signal of group.signals || []) {
        const code = String(signal.signal_code || "").trim();
        const key = code.toUpperCase();
        if (!code || seen.has(key)) continue;
        seen.add(key);
        out.push({ reason_code: code, reason_desc: signal.signal_label || "" });
      }
    }
    return out;
  }

  branchLabel(branch: S09Branch | null): string {
    if (branch === "true") return "True";
    if (branch === "false") return "False";
    if (branch === "escalate") return "Escalate";
    if (branch === "wait") return "Wait";
    return "";
  }

  readonly frmWaitUnits = ["Business days", "Days"];
  readonly leadershipTeams = [
    "FRM leadership",
    "Territory leadership",
    "Hub leadership",
    "Market access leadership",
    "Patient support leadership",
    "Another leadership team",
  ];

  setAssignTeam(team: string): void {
    const patch: Record<string, any> = { team };
    if (team !== "Another leadership team") patch["teamName"] = "";
    this.patchStepValues(patch);
  }

  assignTarget(values: Record<string, any> | undefined): string {
    const team = String(values?.["team"] || "").trim();
    if (team === "Another leadership team") return String(values?.["teamName"] || "").trim() || "Team name not set";
    return team || "Not set";
  }

  fieldVisible(kind: string, values: Record<string, any> | undefined, field: any): boolean {
    if (kind === "assignCase" && field?.key === "teamName") {
      return values?.["team"] === "Another leadership team";
    }
    return true;
  }

  setFrmMaxDays(raw: string | number | null): void {
    const text = raw == null ? "" : String(raw).trim();
    if (!text) {
      this.patchStepValue("maxDays", "");
      return;
    }
    const days = Math.floor(Number(text));
    if (!Number.isFinite(days) || days < 1) return;
    this.patchStepValue("maxDays", String(days));
  }

  linkLabel(id: string): string {
    if (id === "trigger") return "Trigger";
    const step = (this.doc?.steps || []).find((s) => s.id === id);
    return step ? this.stepTitle(step) : "Step";
  }

  libTitle(): string {
    if (this.linkInsert) return "Add between these steps";
    if (this.afterInsert) return "Add after attempt";
    if (this.moduleInsertAt != null) return "Add to module";
    return "Step Library";
  }

  openLinkInsert(from: string, to: string): void {
    this.moduleInsertAt = null;
    this.afterInsert = null;
    this.linkInsert = { from, to };
    this.libQuery = "";
    this.libOpen = true;
    this.rebuildLibrary();
  }

  addLink(from: string, to: string): void {
    if (!this.doc || !to || from === to) return;
    const links = [...(this.doc.links || [])];
    if (links.some((l) => l.from === from && l.to === to)) {
      this.linkPick = "";
      return;
    }
    links.push({ from, to });
    this.linkPick = "";
    this.emit({ ...this.doc, links });
  }

  removeLink(from: string, to: string): void {
    if (!this.doc) return;
    const links = (this.doc.links || []).filter((l) => !(l.from === from && l.to === to));
    this.emit({ ...this.doc, links });
  }

  startBend(
    ev: PointerEvent,
    wire: { from: string; to: string; branch: S09Branch | null }
  ): void {
    ev.preventDefault();
    ev.stopPropagation();
    (ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
    if (this.doc) this.gestureSnapshot = this.cloneDoc(this.doc);
    const at = this.toWorld(ev);
    this.wireSelected = `${wire.from}>${wire.to}>${wire.branch || ""}`;
    this.bendGesture = {
      from: wire.from,
      to: wire.to,
      branch: wire.branch,
      ox: at.x,
      oy: at.y,
      moved: false,
    };
  }

  onWireDown(
    ev: PointerEvent,
    wire: { key: string; from: string; to: string; branch: S09Branch | null; ex: number; ey: number }
  ): void {
    ev.preventDefault();
    ev.stopPropagation();
    const at = this.toWorld(ev);
    this.wireSelected = wire.key;
    if (Math.hypot(at.x - wire.ex, at.y - wire.ey) < 22) {
      this.startLink(ev, wire.from, wire.to, wire.branch);
      return;
    }
    this.startBend(ev, wire);
  }

  resetBend(wire: { from: string; to: string; branch: S09Branch | null }): void {
    if (!this.doc) return;
    const links = (this.doc.links || []).map((link) => {
      if (link.from !== wire.from || link.to !== wire.to || (link.branch || null) !== wire.branch) return link;
      const next = { ...link };
      delete next.bendX;
      delete next.bendY;
      return next;
    });
    this.emit({ ...this.doc, links });
  }

  private sameLink(
    link: S09Link,
    from: string,
    to: string,
    branch: S09Branch | null
  ): boolean {
    return link.from === from && link.to === to && (link.branch || null) === branch;
  }

  removeSelectedWire(): void {
    if (!this.doc || !this.wireSelected) return;
    const [from, to, branch] = this.wireSelected.split(">");
    const links = (this.doc.links || []).filter(
      (link) => !this.sameLink(link, from, to, isS09Branch(branch) ? branch : null)
    );
    this.wireSelected = null;
    this.emit({ ...this.doc, links });
  }

  startDrag(
    ev: PointerEvent,
    target: DragTarget,
    id: string | null,
    origX: number,
    origY: number
  ): void {
    if ((ev.target as HTMLElement)?.closest?.(".s09-resize")) return;
    if ((ev.target as HTMLElement)?.closest?.(".s09-port")) return;
    if ((ev.target as HTMLElement)?.closest?.(".s09-wire-end, .s09-wire-grip")) return;
    if ((ev.target as HTMLElement)?.closest?.(".s09-att")) return;
    ev.preventDefault();
    ev.stopPropagation();
    (ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
    if (this.doc) this.gestureSnapshot = this.cloneDoc(this.doc);
    this.drag = {
      target,
      id,
      startX: ev.clientX,
      startY: ev.clientY,
      origX,
      origY,
      moved: false,
    };
  }

  startResize(ev: PointerEvent, step: S09Step): void {
    ev.preventDefault();
    ev.stopPropagation();
    (ev.currentTarget as HTMLElement)?.setPointerCapture?.(ev.pointerId);
    if (this.doc) this.gestureSnapshot = this.cloneDoc(this.doc);
    const box = this.stepBox(step.id);
    this.resize = {
      id: step.id,
      startX: ev.clientX,
      startY: ev.clientY,
      origW: box ? box.right - box.left : step.w || 220,
      origH: box ? box.bottom - box.top : step.h || 96,
    };
    this.selectStep(step.id);
  }

  @HostListener("document:pointermove", ["$event"])
  onPointerMove(ev: PointerEvent): void {
    if (this.bendGesture && this.doc) {
      const at = this.toWorld(ev);
      const g = this.bendGesture;
      if (!g.moved && Math.hypot(at.x - g.ox, at.y - g.oy) < 3) return;
      g.moved = true;
      const links = (this.doc.links || []).map((link) =>
        this.sameLink(link, g.from, g.to, g.branch) ? { ...link, bendX: at.x, bendY: at.y } : link
      );
      this.doc = { ...this.doc, links };
      return;
    }
    if (this.linkGesture) {
      const at = this.toWorld(ev);
      const moved = Math.hypot(at.x - this.linkGesture.ox, at.y - this.linkGesture.oy) > 4;
      const hit = this.nearestInPort(at.x, at.y, this.linkGesture.from);
      this.linkGesture = {
        ...this.linkGesture,
        x: at.x,
        y: at.y,
        moved,
        over: hit?.id || null,
        side: hit?.side || null,
      };
      return;
    }
    if (this.resize && this.doc) {
      const dw = ev.clientX - this.resize.startX;
      const dh = ev.clientY - this.resize.startY;
      const w = Math.max(200, Math.min(960, this.resize.origW + dw));
      const h = Math.max(72, Math.min(800, this.resize.origH + dh));
      const steps = this.doc.steps.map((s) =>
        s.id === this.resize!.id ? { ...s, w, h } : s
      );
      this.doc = { ...this.doc, steps };
      return;
    }
    if (!this.drag || !this.doc) return;
    const dx = ev.clientX - this.drag.startX;
    const dy = ev.clientY - this.drag.startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) this.drag.moved = true;
    const x = Math.max(8, this.drag.origX + dx);
    const y = Math.max(8, this.drag.origY + dy);
    if (this.drag.target === "step" && this.drag.id) {
      const steps = this.doc.steps.map((s) =>
        s.id === this.drag!.id ? { ...s, x, y } : s
      );
      this.doc = { ...this.doc, steps };
    } else if (this.drag.target === "trigger") {
      this.doc = {
        ...this.doc,
        layout: {
          ...(this.doc.layout || {}),
          trigger: { ...this.triggerGeom, x, y },
        },
      };
    }
  }

  @HostListener("document:pointerup")
  onPointerUp(): void {
    if (this.bendGesture) {
      const moved = this.bendGesture.moved;
      this.bendGesture = null;
      if (moved && this.doc) {
        if (this.gestureSnapshot) this.pushHistory(this.gestureSnapshot);
        this.emit(this.doc, { history: false });
      }
      this.gestureSnapshot = null;
      return;
    }
    if (this.linkGesture) {
      this.finishLink();
      return;
    }
    if (this.resize) {
      this.resize = null;
      if (this.doc) {
        if (this.gestureSnapshot) this.pushHistory(this.gestureSnapshot);
        this.emit(this.doc, { history: false });
      }
      this.gestureSnapshot = null;
      this.suppressClick = true;
      return;
    }
    if (!this.drag) return;
    const d = this.drag;
    this.drag = null;
    if (this.doc && d.moved) {
      if (this.gestureSnapshot) this.pushHistory(this.gestureSnapshot);
      this.emit(this.doc, { history: false });
      this.suppressClick = true;
    } else if (!d.moved) {
      if (d.target === "step" && d.id) this.selectStep(d.id);
      else if (d.target === "trigger") this.openTrigger();
    }
    this.gestureSnapshot = null;
  }

  onNodeClick(step: S09Step, ev: MouseEvent): void {
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    if ((ev.target as HTMLElement)?.closest?.(".s09-att")) return;
    if ((ev.target as HTMLElement)?.closest?.(".s09-resize")) return;
    this.selectStep(step.id);
  }

  autoArrange(): void {
    if (!this.doc) return;
    const trig = { x: 120, y: 28, w: 240 };
    let y = 96;
    const steps = this.doc.steps.map((s) => {
      const w = s.w || (s.kind === "outreachModule" ? 340 : 220);
      const x = Math.max(24, trig.x + (trig.w - w) / 2);
      const next = { ...s, x, y, w };
      y += (s.h || (s.kind === "outreachModule" ? 150 : 96)) + 36;
      return next;
    });
    this.emit({ ...this.doc, steps, layout: { trigger: trig } });
  }

  private loadLookups(): void {
    const brandKey = Number(this.brandKey) || 200104;
    this.workflowService.getReasonCodes(brandKey).subscribe((rows) => {
      this.reasonCodes = rows || [];
    });
    this.workflowService.getEntrySignals(brandKey).subscribe((groups) => {
      this.signalGroups = groups || [];
      this.signalsHint = this.signalGroups.length
        ? ""
        : "No signals configured for this brand. Open the Signals tab to add groups and reason codes.";
    });
    this.workflowService.getTriggerConditions(brandKey).subscribe((rows) => {
      this.allConditions = rows || [];
      this.conditionsHint = this.allConditions.length
        ? ""
        : "No conditions configured. Run 05_create_trigger_condition_config.sql and 06_seed_trigger_conditions.sql.";
    });
    this.workflowService.getCaseTypes(brandKey, true).subscribe((rows) => {
      this.caseTypes = rows || [];
    });
    this.syncCaseStages();
    this.workflowService.getTemplates(brandKey).subscribe((rows) => {
      const loaded = rows?.length
        ? rows.map((t) => ({
            id: t.template_code || t.id,
            name: t.template_name || t.name,
            kind: t.kind,
            audience: (t as any).audience || "",
            status: t.source_status,
            source: t.source_status,
            subject: t.subject || "",
            body: t.body || "",
          }))
        : [...(RZ_TEMPLATES as any[])];
      const ids = new Set(loaded.map((t) => t.id));
      const designTpls = (RZ_TEMPLATES as any[]).filter(
        (t) => t.audience && !ids.has(t.id)
      );
      this.templates = [...loaded, ...designTpls];
    });
  }

  readonly emailAudiences = ["HCP office", "Patient"];

  gateList(kind: string): string[] {
    const custom = (catalogEntry(kind) as any)?.gates;
    if (Array.isArray(custom) && custom.length) return custom;
    return this.gates;
  }

  gateText(kind: string): string {
    const custom = (catalogEntry(kind) as any)?.gates;
    if (Array.isArray(custom) && custom.length) return `Check · ${custom.join(", ")}`;
    return "Six-check gate";
  }

  hasCatalogGates(kind: string): boolean {
    const custom = (catalogEntry(kind) as any)?.gates;
    return Array.isArray(custom) && custom.length > 0;
  }

  tplMap(values: Record<string, any> | undefined): Record<string, string> {
    const raw = values?.["tpl"];
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const out: Record<string, string> = {};
      Object.keys(raw).forEach((key) => {
        out[key] = raw[key] == null ? "" : String(raw[key]);
      });
      if (Object.keys(out).length) return out;
    }
    return { "HCP office": "" };
  }

  tplAudiencesOn(values: Record<string, any> | undefined): string[] {
    return Object.keys(this.tplMap(values));
  }

  tplOn(values: Record<string, any> | undefined, audience: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.tplMap(values), audience);
  }

  tplId(values: Record<string, any> | undefined, audience: string): string {
    return this.tplMap(values)[audience] || "";
  }

  toggleTplAudience(current: Record<string, string>, audience: string): Record<string, string> {
    const next: Record<string, string> = { ...current };
    if (Object.prototype.hasOwnProperty.call(next, audience)) delete next[audience];
    else next[audience] = "";
    if (!Object.keys(next).length) next["HCP office"] = "";
    return next;
  }

  setTplId(current: Record<string, string>, audience: string, id: string): Record<string, string> {
    return { ...current, [audience]: id };
  }

  templatesFor(audience: string): any[] {
    const matched = this.templates.filter((t) => t.audience === audience);
    return matched.length ? matched : this.templates;
  }

  templateById(tplId: string): any | null {
    return (
      this.templates.find((x) => x.id === tplId || x.name === tplId) || null
    );
  }

  templateName(tplId: string): string {
    const t = this.templateById(tplId);
    return t?.name || tplId || "Pick a script";
  }

  get selectedTemplate(): any | null {
    const id = String(this.selected?.values?.["template"] || "");
    return id ? this.templateById(id) : null;
  }

  pickCondition(label: string): void {
    const next = this.doc?.meta?.threshold === label ? "" : label;
    this.patchMeta("threshold", next);
  }

  toggleEntrySignal(code: string): void {
    if (!this.doc) return;
    const codes = [...(this.doc.meta.codes || [])];
    const i = codes.indexOf(code);
    if (i >= 0) codes.splice(i, 1);
    else codes.push(code);
    this.patchMeta("codes", codes);
  }

  hasEntrySignal(code: string): boolean {
    return (this.doc?.meta?.codes || []).includes(code);
  }

  signalTip(s: { signal_code?: string; signal_label?: string }): string {
    return entrySignalTooltip(s);
  }

  rebuildLibrary(): void {
    const q = this.libQuery.trim().toLowerCase();
    const forModule = this.moduleInsertAt != null || this.afterInsert != null;
    const groups: Record<string, any[]> = {};
    let count = 0;
    Object.keys(CATALOG).forEach((kind) => {
      const c = (CATALOG as any)[kind];
      if (!c?.label || !c?.group) return;
      if (forModule && (kind === "outreachModule" || c.future)) return;
      const hay = `${c.label} ${c.sub || ""} ${c.group}`.toLowerCase();
      if (q && !hay.includes(q)) return;
      (groups[c.group] ||= []).push({
        kind,
        label: c.label,
        sub: c.sub || "",
        colour: c.colour || "#1360DF",
        path: c.path || "M5 12h14",
        future: !!c.future,
      });
      count++;
    });
    const ordered = [
      ...LIBRARY_GROUPS.filter((g) => groups[g]?.length),
      ...Object.keys(groups).filter((g) => !LIBRARY_GROUPS.includes(g)),
    ];
    this.library = ordered.map((group) => ({ group, items: groups[group] }));
    this.libraryCount = count;
  }

  onLibQuery(): void {
    this.rebuildLibrary();
  }

  openLibrary(): void {
    this.moduleInsertAt = null;
    this.afterInsert = null;
    this.libOpen = true;
    this.rebuildLibrary();
    setTimeout(() => {
      const input = this.host.nativeElement.querySelector(
        ".s09-lib-search"
      ) as HTMLInputElement | null;
      input?.focus();
    }, 0);
  }

  closeLibrary(): void {
    this.libOpen = false;
    this.libQuery = "";
    this.moduleInsertAt = null;
    this.afterInsert = null;
    this.linkInsert = null;
    this.rebuildLibrary();
  }

  openValidation(): void {
    this.closeInspector();
    this.valOpen = true;
  }

  closeValidation(): void {
    this.valOpen = false;
  }

  toggleValidation(): void {
    if (this.valOpen && !this.hasInspector) this.closeValidation();
    else this.openValidation();
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    if (this.libOpen) {
      this.closeLibrary();
      return;
    }
    if (this.hasInspector) {
      this.closeInspector();
      return;
    }
    if (this.valOpen) this.closeValidation();
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown(ev: KeyboardEvent): void {
    const t = ev.target as HTMLElement | null;
    const tag = (t?.tagName || "").toLowerCase();
    const typing =
      tag === "input" ||
      tag === "textarea" ||
      tag === "select" ||
      !!t?.isContentEditable;
    if (typing) return;

    const mod = ev.ctrlKey || ev.metaKey;
    if (mod && ev.key.toLowerCase() === "z" && !ev.shiftKey) {
      ev.preventDefault();
      this.undo();
      return;
    }
    if (mod && (ev.key.toLowerCase() === "y" || (ev.key.toLowerCase() === "z" && ev.shiftKey))) {
      ev.preventDefault();
      this.redo();
      return;
    }
    if (mod && ev.key.toLowerCase() === "c" && this.canCopyStep) {
      ev.preventDefault();
      this.copySelected();
      return;
    }
    if (mod && ev.key.toLowerCase() === "v" && this.canPasteStep) {
      ev.preventDefault();
      this.pasteStep();
      return;
    }
    if ((ev.key === "Delete" || ev.key === "Backspace") && this.wireSelected) {
      ev.preventDefault();
      this.removeSelectedWire();
      return;
    }
    if ((ev.key === "Delete" || ev.key === "Backspace") && this.canDeleteStep) {
      ev.preventDefault();
      this.removeSelected();
    }
  }

  @HostListener("document:mousedown", ["$event"])
  onDocMouseDown(ev: MouseEvent): void {
    const t = ev.target as HTMLElement | null;
    if (!t) return;
    if (!this.libOpen) return;
    if (t.closest(".s09-lib") || t.closest(".s09-lib-toggle")) return;
    this.closeLibrary();
  }

  addCanvasAttempt(): void {
    if (!this.doc) return;
    const before = new Set(this.doc.steps.map((s) => s.id));
    const next = addAttemptSteps(this.doc);
    this.emit(next);
    const added = next.steps.find((s) => !before.has(s.id));
    if (added) this.selectStep(added.id);
  }

  stepTitle(step: S09Step): string {
    if (step.kind === "attempt") {
      const name = String(step.values?.["name"] || "").trim();
      return name || "Attempt";
    }
    return catalogEntry(String(step.kind))?.label || String(step.kind);
  }

  attemptWhen(): string[] {
    const field = (catalogEntry("attempt")?.fields || []).find((f: any) => f.key === "runsAfter");
    return field?.options || [];
  }

  /** Contact fields from template creation. Case data fields are not recipients. */
  readonly contactFields = [
    "Office contact",
    "SP contact",
    "FRM name",
    "Hub/SP name",
    "Patient name",
  ];
  toPick = "";
  ccPick = "";
  toEmail = "";
  ccEmail = "";
  mailHint = "";

  mailList(value: any): string[] {
    const raw = Array.isArray(value) ? value : value ? [value] : [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of raw) {
      const text = String(item || "").trim();
      const key = text.toLowerCase();
      if (!text || seen.has(key)) continue;
      seen.add(key);
      out.push(text);
    }
    return out;
  }

  mailLabel(value: string): string {
    const match = /^\[(.+)\]$/.exec(value);
    return match ? match[1] : value;
  }

  contactChoices(key: "to" | "cc"): string[] {
    const used = new Set(this.mailList(this.selected?.values?.[key]).map((item) => item.toLowerCase()));
    return this.contactFields.filter((name) => !used.has(`[${name}]`.toLowerCase()));
  }

  addAttemptMailField(key: "to" | "cc"): void {
    const field = key === "to" ? this.toPick : this.ccPick;
    if (!field) return;
    this.mailHint = "";
    this.addMail(key, `[${field}]`);
    if (key === "to") this.toPick = "";
    else this.ccPick = "";
  }

  addAttemptMailText(key: "to" | "cc"): void {
    const raw = (key === "to" ? this.toEmail : this.ccEmail).trim();
    if (!raw) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      this.mailHint = "Enter a full email address, such as name@company.com.";
      return;
    }
    this.mailHint = "";
    this.addMail(key, raw);
    if (key === "to") this.toEmail = "";
    else this.ccEmail = "";
  }

  removeAttemptMail(key: "to" | "cc", item: string): void {
    const next = this.mailList(this.selected?.values?.[key]).filter(
      (entry) => entry.toLowerCase() !== item.toLowerCase()
    );
    this.patchStepValue(key, next);
  }

  mailSummary(value: any): string {
    const items = this.mailList(value).map((item) => this.mailLabel(item));
    return items.length ? items.join(" · ") : "None";
  }

  private addMail(key: "to" | "cc", item: string): void {
    const next = this.mailList(this.selected?.values?.[key]);
    if (next.some((entry) => entry.toLowerCase() === item.toLowerCase())) return;
    this.patchStepValue(key, [...next, item]);
  }

  private clearMailDrafts(): void {
    this.toPick = "";
    this.ccPick = "";
    this.toEmail = "";
    this.ccEmail = "";
    this.mailHint = "";
  }

  attemptGoOptions(step: S09Step | null | undefined, includeNext: boolean): { id: string; label: string }[] {
    const opts: { id: string; label: string }[] = [{ id: "end", label: "End workflow" }];
    if (includeNext) opts.push({ id: "next", label: "Next attempt" });
    for (const other of this.doc?.steps || []) {
      if (!other || other.id === step?.id) continue;
      const label =
        other.kind === "attempt"
          ? String(other.values?.["name"] || "Attempt")
          : catalogEntry(String(other.kind))?.label || String(other.kind);
      opts.push({ id: other.id, label });
    }
    return opts;
  }

  attemptTargetLabel(target: any): string {
    const id = String(target || "");
    if (!id || id === "end") return "End workflow";
    if (id === "next") return "Next attempt";
    const step = (this.doc?.steps || []).find((s) => s.id === id);
    if (!step) return "Choose a step";
    return this.stepTitle(step);
  }

  addStep(kind: string, future: boolean): void {
    if (future || !this.doc) return;
    if (this.afterInsert) {
      if (kind === "outreachModule") return;
      const target = this.afterInsert;
      this.insertAfterStep(target.attempt, target.at, kind);
      this.closeLibrary();
      return;
    }
    if (this.moduleInsertAt != null) {
      if (kind === "outreachModule") return;
      const at = this.moduleInsertAt;
      this.insertModuleStep(kind, at);
      this.closeLibrary();
      return;
    }
    if (this.linkInsert) {
      const target = this.linkInsert;
      const beforeIds = new Set(this.doc.steps.map((s) => s.id));
      const next = insertStepBetween(this.doc, kind, target.from, target.to);
      this.emit(next);
      const added = next.steps.find((s) => !beforeIds.has(s.id));
      this.closeLibrary();
      if (added) this.selectStep(added.id);
      return;
    }
    const beforeIds = new Set(this.doc.steps.map((s) => s.id));
    const next = addLibraryStep(this.doc, kind);
    this.emit(next);
    const added = next.steps.find((s) => !beforeIds.has(s.id));
    this.closeLibrary();
    if (added) this.selectStep(added.id);
  }

  selectStep(id: string): void {
    this.valOpen = false;
    this.selectedId = id;
    this.selectedAttempt = null;
    this.selectedModStepId = null;
    this.inspMode = "step";
    this.gateRefused = false;
    this.wireSelected = null;
    this.clearMailDrafts();
  }

  selectAttempt(stepId: string, idx: number, ev: Event): void {
    ev.stopPropagation();
    this.valOpen = false;
    this.selectedId = stepId;
    this.selectedAttempt = idx;
    this.inspMode = "attempt";
    this.gateRefused = false;
  }

  openTrigger(): void {
    this.closeLibrary();
    this.valOpen = false;
    this.selectedId = null;
    this.selectedAttempt = null;
    this.inspMode = "trigger";
    this.gateRefused = false;
  }

  closeInspector(): void {
    this.inspMode = "none";
    this.selectedId = null;
    this.selectedAttempt = null;
    this.gateRefused = false;
  }

  backToModule(): void {
    if (!this.selectedId) return;
    this.selectedAttempt = null;
    this.inspMode = "step";
  }

  get selected(): S09Step | null {
    return this.doc?.steps?.find((s) => s.id === this.selectedId) || null;
  }

  get currentAttempt(): any | null {
    const s = this.selected;
    if (!s?.mod || this.selectedAttempt == null) return null;
    return s.mod.attempts[this.selectedAttempt] || null;
  }

  get selectedMod(): S09OutreachMod | null {
    const s = this.selected;
    if (s?.kind !== "outreachModule") return null;
    return s.mod || null;
  }

  get isOutreachStep(): boolean {
    return this.inspMode === "step" && this.selected?.kind === "outreachModule";
  }

  get isGatedStep(): boolean {
    return !!(
      this.inspMode === "step" &&
      this.selected &&
      catalogEntry(this.selected.kind)?.gated
    );
  }

  get hasInspector(): boolean {
    return this.inspMode !== "none";
  }

  /** Right column reserved for inspector or validation drawer. */
  get rightRailOpen(): boolean {
    return this.hasInspector || this.valOpen;
  }

  get triggerAccent(): string {
    return triggerMeta(this.doc?.trigger || "status").colour;
  }

  get triggerPath(): string {
    return triggerMeta(this.doc?.trigger || "status").path;
  }

  get triggerLabel(): string {
    return triggerMeta(this.doc?.trigger || "status").label || this.doc?.trigger || "Trigger";
  }

  get attemptPreview(): any | null {
    const a = this.currentAttempt;
    if (!a?.tpl) return null;
    return this.templateById(a.tpl);
  }

  catalog(kind: string) {
    return catalogEntry(kind);
  }

  isGated(kind: string): boolean {
    return !!catalogEntry(kind)?.gated;
  }

  readonly delayFromPrevious = "Days from the previous step";

  waitFrom(values: Record<string, any> | undefined): string {
    return String(values?.["from"] || "").trim() || "Since the last step";
  }

  isPreviousDayDelay(value: any): boolean {
    return String(value || "").trim() === this.delayFromPrevious;
  }

  /** Step that feeds this one. The Wait / Delay parameter reads its days. */
  previousStep(stepId?: string): S09Step | null {
    const id = stepId || this.selected?.id || "";
    if (!id || !this.doc) return null;
    const link = (this.doc.links || []).find((l) => l.to === id && l.from !== "trigger");
    if (!link) return null;
    return (this.doc.steps || []).find((s) => s.id === link.from) || null;
  }

  previousDelayHint(stepId?: string): string {
    const prev = this.previousStep(stepId);
    if (prev?.kind === "frmDecision") {
      const unit = String(prev.values?.["unit"] || "Business days").toLowerCase();
      const max = String(prev.values?.["maxDays"] || "").trim();
      const cap = max ? `, up to ${max}` : "";
      return `This step waits the ${unit} the FRM enters on ${this.stepTitle(prev)}${cap}.`;
    }
    if (prev) return `This step waits the days recorded on ${this.stepTitle(prev)}.`;
    return "This step waits the number of days the previous step recorded. Connect it after the FRM Decision Wait branch.";
  }

  delayCardText(stepId: string, delay: string): string {
    if (!this.isPreviousDayDelay(delay)) return delay;
    const prev = this.previousStep(stepId);
    if (prev?.kind === "frmDecision") return "Days the FRM entered";
    return this.delayFromPrevious;
  }

  inspectorFields(kind: string): any[] {
    const fields = catalogEntry(kind)?.fields || [];
    if (kind === "createCase") return fields.filter((f: any) => f.key !== "type");
    return fields;
  }

  nodeRows(step: S09Step): { label: string; value: string; chips?: string[] }[] {
    if (step.kind === "wait") {
      const delay = String(step.values?.["delay"] || "").trim();
      return [
        { label: "Delay", value: delay ? this.delayCardText(step.id, delay) : "Not set" },
        { label: "Count from", value: this.waitFrom(step.values) },
        { label: "Stop", value: String(step.values?.["stop"] || "None") },
      ];
    }
    if (step.kind === "condition" && step.values?.["left"] === "Latest status code") {
      const chips = String(step.values?.["right"] || "")
        .split(/[,·]/)
        .map((code) => code.trim())
        .filter(Boolean);
      return [
        { label: "Check", value: "Latest status code" },
        { label: "Operator", value: String(step.values?.["op"] || "Not set") },
        { label: "Codes", value: chips.length ? "" : "Not set", chips },
      ];
    }
    if (step.kind === "attempt") {
      const template = String(step.values?.["template"] || "");
      return [
        { label: "Runs", value: String(step.values?.["runsAfter"] || "Not set") },
        { label: "Template", value: template ? this.templateName(template) : "Not set" },
        { label: "To", value: this.mailSummary(step.values?.["to"]) },
        { label: "Cc", value: this.mailSummary(step.values?.["cc"]) },
        { label: "After this attempt", value: this.attemptTargetLabel(step.values?.["onOutcome"]) },
      ];
    }
    if (step.kind === "frmDecision") {
      const unit = String(step.values?.["unit"] || "Business days");
      const max = String(step.values?.["maxDays"] || "").trim();
      const escalate = this.branchTarget(step.id, "escalate");
      const wait = this.branchTarget(step.id, "wait");
      return [
        { label: "Ask", value: String(step.values?.["ask"] || "Not set") },
        {
          label: "Wait",
          value: max ? `FRM enters the days, up to ${max} ${unit.toLowerCase()}` : `FRM enters the ${unit.toLowerCase()}`,
        },
        { label: "Escalate", value: escalate ? this.linkLabel(escalate) : "Not set" },
        { label: "Then", value: wait ? this.linkLabel(wait) : "Not set" },
      ];
    }
    if (step.kind === "assignCase") {
      const note = String(step.values?.["note"] || "").trim();
      const rows = [{ label: "Assign to", value: this.assignTarget(step.values) }];
      if (note) rows.push({ label: "Note", value: note });
      return rows;
    }
    if (step.kind === "outreachModule" && step.mod) {
      const m = step.mod;
      const days = m.attempts.map((a) => a.day || "?").join(" · ");
      const scripts = m.attempts
        .map((a) => this.templateName(a.tpl || a.tplText || ""))
        .filter((x) => x && x !== "Pick a script")
        .join(" → ");
      return [
        { label: "Scripts", value: scripts || "Not set" },
        {
          label: "Attempt days",
          value: days
            ? `Day ${days}${m.every ? `, then every ${m.every}` : ""}`
            : "Not set",
        },
        {
          label: "When attempts run out",
          value: m.escalate ? "Escalate to FRM leadership" : "No escalation",
        },
      ];
    }
    if (step.kind === "createCase") {
      const attached = String(this.doc?.meta?.caseType || "").trim();
      const rest = (catalogEntry("createCase")?.fields || [])
        .filter((f: any) => f.key !== "type" && this.fieldFilled(step.values, f))
        .slice(0, 4)
        .map((f: any) => ({
          label: f.label,
          value: this.fieldText(step.values, f),
        }));
      return [{ label: "Case type", value: attached || "Not set on the trigger" }, ...rest];
    }
    const cat = catalogEntry(String(step.kind));
    if (!cat?.fields) return [];
    return cat.fields
      .filter((f: any) => this.fieldFilled(step.values, f))
      .slice(0, 5)
      .map((f: any) => ({
        label: f.label,
        value: this.fieldText(step.values, f),
      }));
  }

  attemptTitle(a: any): string {
    const raw = a.tpl || a.tplText || "";
    if (!raw) return "Script not set";
    return this.templateName(raw);
  }

  emit(doc: S09DesignDoc, opts?: { history?: boolean }): void {
    if (opts?.history !== false && this.doc) {
      this.pushHistory(this.doc);
    }
    this.docChange.emit(doc);
  }

  undo(): void {
    if (!this.canUndo || !this.doc) return;
    this.redoStack.push(this.cloneDoc(this.doc));
    const prev = this.undoStack.pop()!;
    this.closeInspector();
    this.docChange.emit(prev);
  }

  redo(): void {
    if (!this.canRedo || !this.doc) return;
    this.undoStack.push(this.cloneDoc(this.doc));
    const next = this.redoStack.pop()!;
    this.closeInspector();
    this.docChange.emit(next);
  }

  patchStepValue(key: string, value: any): void {
    this.patchStepValues({ [key]: value });
  }

  patchStepValues(patch: Record<string, any>): void {
    if (!this.selected || !this.doc) return;
    const steps = this.doc.steps.map((s) =>
      s.id === this.selected!.id
        ? { ...s, values: { ...s.values, ...patch } }
        : s
    );
    this.emit({ ...this.doc, steps });
  }

  patchMeta(key: string, value: any): void {
    if (!this.doc) return;
    this.emit({
      ...this.doc,
      meta: { ...this.doc.meta, [key]: value },
      triggerValue: key === "threshold" ? value : this.doc.triggerValue,
      triggerSource: key === "feed" ? value : this.doc.triggerSource,
    });
  }

  pickCaseType(raw: number | string | null): void {
    if (!this.doc) return;
    const key = raw == null || raw === "" ? null : Number(raw);
    const picked = this.caseTypes.find((t) => t.typeKey === key) || null;
    this.emit({
      ...this.doc,
      meta: {
        ...this.doc.meta,
        caseType: picked?.name || "",
        caseTypeKey: picked?.typeKey ?? null,
        caseTypeCode: picked?.typeCode || "",
      },
    });
    this.fetchCaseStages(picked?.typeKey ?? null);
  }

  private syncCaseStages(): void {
    const key = this.doc?.meta?.caseTypeKey ? Number(this.doc.meta.caseTypeKey) : null;
    if (key === this.loadedStageKey) return;
    this.fetchCaseStages(key);
  }

  private fetchCaseStages(key: number | null): void {
    this.loadedStageKey = key;
    if (!key) {
      this.caseStages = [];
      return;
    }
    const brandKey = Number(this.brandKey) || 200104;
    this.workflowService.getCaseFlow(brandKey, key).subscribe((rows) => {
      if (this.loadedStageKey !== key) return;
      this.caseStages = rows || [];
    });
  }

  isChoiceField(field: any): boolean {
    return !!(field?.options?.length || field?.key === "stage");
  }

  customDayCount(value: any): number | null {
    const match = String(value || "").trim().match(/^(\d+)\b/);
    if (!match) return null;
    const n = Number(match[1]);
    return n > 0 ? n : null;
  }

  customDayUnit(value: any): "minutes" | "business days" | "days" {
    const text = String(value || "");
    if (/minute/i.test(text)) return "minutes";
    if (!text.trim() || /business/i.test(text)) return "business days";
    return "days";
  }

  formatCustomDays(raw: number | string | null, unit: string): string {
    const n = Math.floor(Number(raw));
    if (!n || n < 1) return "";
    if (unit === "minutes") return n === 1 ? "1 minute" : `${n} minutes`;
    if (unit === "days") return n === 1 ? "1 day" : `${n} days`;
    return n === 1 ? "1 business day" : `${n} business days`;
  }

  choiceOptions(field: any): string[] {
    if (field?.key === "stage") return this.caseStages.map((s) => s.stage_name);
    return field?.options || [];
  }

  get stageHint(): string {
    if (this.doc?.meta?.caseTypeKey && this.caseStages.length) return "";
    if (!this.doc?.meta?.caseTypeKey) {
      return "Select a case type on the trigger. Add stages on the landing Case Flow tab.";
    }
    return "No stages for this case type. Add them on the landing Case Flow tab.";
  }

  patchTrigger(id: string): void {
    if (!this.doc) return;
    this.emit({ ...this.doc, trigger: id });
  }

  private withMod(fn: (mod: S09OutreachMod) => void): void {
    if (!this.selected || this.selected.kind !== "outreachModule" || !this.doc)
      return;
    const mod: S09OutreachMod = JSON.parse(
      JSON.stringify(this.selected.mod || defaultMod())
    );
    fn(mod);
    const steps = this.doc.steps.map((s) =>
      s.id === this.selected!.id ? { ...s, mod } : s
    );
    this.emit({ ...this.doc, steps });
  }

  patchMod(field: keyof S09OutreachMod, value: any): void {
    this.withMod((mod) => {
      (mod as any)[field] = value;
    });
  }

  patchAttempt(field: string, value: any): void {
    if (this.selectedAttempt == null) return;
    const idx = this.selectedAttempt;
    this.withMod((mod) => {
      if (!mod.attempts[idx]) return;
      (mod.attempts[idx] as any)[field] = value;
    });
  }

  patchAttemptRow(idx: number, field: string, value: any): void {
    this.withMod((mod) => {
      if (!mod.attempts[idx]) return;
      (mod.attempts[idx] as any)[field] = value;
    });
  }

  addAttempt(): void {
    this.withMod((mod) => {
      mod.attempts.push({
        day: "",
        tpl: "",
        tplText: "",
        channel: "",
        hub: "",
        ask: "",
        after: [],
      });
    });
  }

  removeAttemptAt(idx: number): void {
    this.withMod((mod) => {
      if (mod.attempts.length <= 1) return;
      mod.attempts.splice(idx, 1);
    });
    if (this.selectedAttempt != null) {
      if (this.selectedAttempt === idx) this.backToModule();
      else if (this.selectedAttempt > idx)
        this.selectedAttempt = this.selectedAttempt - 1;
    }
  }

  removeCurrentAttempt(): void {
    if (this.selectedAttempt == null) return;
    this.removeAttemptAt(this.selectedAttempt);
  }

  copySelected(): void {
    if (!this.canCopyStep || !this.selected) return;
    this.stepClipboard = JSON.parse(JSON.stringify(this.selected)) as S09Step;
    this.pasteNudge = 0;
  }

  pasteStep(): void {
    if (!this.doc || !this.stepClipboard) return;
    this.pasteNudge += 1;
    const copy = this.clonePastedStep(this.stepClipboard, this.pasteNudge);
    this.emit({ ...this.doc, steps: [...this.doc.steps, copy] });
    this.selectStep(copy.id);
  }

  private clonePastedStep(source: S09Step, nudge: number): S09Step {
    const step = JSON.parse(JSON.stringify(source)) as S09Step;
    const oldId = step.id;
    step.id = nextStepId();
    const shift = 36 * nudge;
    step.x = (Number(step.x) || 120) + shift;
    step.y = (Number(step.y) || 120) + shift;
    if (step.values && step.values["onOutcome"] === oldId) step.values["onOutcome"] = step.id;
    if (step.mod) {
      step.mod.steps = (step.mod.steps || []).map((item) => ({ ...item, id: nextModuleStepId() }));
      step.mod.attempts = (step.mod.attempts || []).map((attempt) => ({
        ...attempt,
        after: (attempt.after || []).map((item) => ({ ...item, id: nextModuleStepId() })),
      }));
    }
    if (step.kind === "attempt") {
      const nextNo =
        (this.doc?.steps || []).reduce(
          (max, item) => Math.max(max, Number(item.values?.["attemptNo"]) || 0),
          0
        ) + 1;
      const oldNo = Number(step.values?.["attemptNo"]) || 0;
      const name = String(step.values?.["name"] || "").trim();
      step.values["attemptNo"] = nextNo;
      step.values["name"] =
        !name || name === `Attempt ${oldNo}` ? `Attempt ${nextNo}` : this.copiedLabel(name);
    }
    return step;
  }

  private copiedLabel(name: string): string {
    const match = /^(.*?)(?: copy(?: (\d+))?)?$/.exec(name);
    const base = (match?.[1] || name).trim();
    const n = match?.[2] ? Number(match[2]) + 1 : name.endsWith(" copy") ? 2 : 1;
    return n === 1 ? `${base} copy` : `${base} copy ${n}`;
  }

  removeSelected(): void {
    if (!this.selected || !this.doc) return;
    const label =
      catalogEntry(this.selected.kind)?.label || this.selected.kind || "step";
    if (!confirm(`Delete “${label}” from this workflow?\n\nYou can Undo (Ctrl+Z) after.`)) {
      return;
    }
    const removed = this.selected!.id;
    const steps = this.doc.steps.filter((s) => s.id !== removed);
    const links = this.linksAfterDelete(removed, this.doc.links || []);
    this.closeInspector();
    this.emit({ ...this.doc, steps, links });
  }

  private linksAfterDelete(stepId: string, links: S09Link[]): S09Link[] {
    const incoming = links.filter((l) => l.to === stepId);
    const outgoing = links.filter((l) => l.from === stepId);
    const rest = links.filter((l) => l.from !== stepId && l.to !== stepId);
    if (incoming.length === 1 && outgoing.length === 1) {
      const from = incoming[0].from;
      const to = outgoing[0].to;
      if (from !== to && !rest.some((l) => l.from === from && l.to === to)) {
        rest.push(
          incoming[0].branch ? { from, to, branch: incoming[0].branch } : { from, to }
        );
      }
    }
    return rest;
  }

  deleteStepFromCanvas(stepId: string, ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.selectStep(stepId);
    this.removeSelected();
  }

  tryRemoveGate(): void {
    this.gateRefused = true;
  }

  openModulePicker(at: number): void {
    this.afterInsert = null;
    this.moduleInsertAt = at;
    this.libQuery = "";
    this.libOpen = true;
    this.rebuildLibrary();
    setTimeout(() => {
      const input = this.host.nativeElement.querySelector(
        ".s09-lib-search"
      ) as HTMLInputElement | null;
      input?.focus();
    }, 0);
  }

  insertModuleStep(kind: string, at: number): void {
    const step: S09ModuleStep = {
      id: nextModuleStepId(),
      kind,
      values: blankStepValues(kind),
    };
    this.withMod((mod) => {
      if (!Array.isArray(mod.steps)) mod.steps = [];
      const index = Math.max(0, Math.min(at, mod.steps.length));
      mod.steps.splice(index, 0, step);
    });
    this.selectedModStepId = step.id;
  }

  openAfterPicker(attempt: number, at: number): void {
    this.moduleInsertAt = null;
    this.afterInsert = { attempt, at };
    this.libQuery = "";
    this.libOpen = true;
    this.rebuildLibrary();
    setTimeout(() => {
      const input = this.host.nativeElement.querySelector(
        ".s09-lib-search"
      ) as HTMLInputElement | null;
      input?.focus();
    }, 0);
  }

  insertAfterStep(
    attempt: number,
    at: number,
    kind: string,
    values?: Record<string, any>
  ): void {
    const step: S09ModuleStep = {
      id: nextModuleStepId(),
      kind,
      values: values || blankStepValues(kind),
    };
    this.withMod((mod) => {
      const row = mod.attempts[attempt];
      if (!row) return;
      if (!Array.isArray(row.after)) row.after = [];
      const index = Math.max(0, Math.min(at, row.after.length));
      row.after.splice(index, 0, step);
    });
    this.selectedModStepId = step.id;
  }

  removeAfterStep(attempt: number, index: number, ev: Event): void {
    ev.stopPropagation();
    const id = this.selectedMod?.attempts?.[attempt]?.after?.[index]?.id;
    this.withMod((mod) => {
      const row = mod.attempts[attempt];
      if (!row?.after) return;
      row.after.splice(index, 1);
    });
    if (this.selectedModStepId === id) this.selectedModStepId = null;
  }

  patchAfterValue(attempt: number, id: string, key: string, value: any): void {
    this.withMod((mod) => {
      const step = mod.attempts[attempt]?.after?.find((s) => s.id === id);
      if (!step) return;
      step.values = { ...step.values, [key]: value };
    });
  }

  toggleAfterMulti(attempt: number, id: string, key: string, option: string): void {
    this.withMod((mod) => {
      const step = mod.attempts[attempt]?.after?.find((s) => s.id === id);
      if (!step) return;
      const cur = this.multiValues(step.values, key);
      const next = cur.includes(option) ? cur.filter((d) => d !== option) : [...cur, option];
      step.values = { ...step.values, [key]: next };
    });
  }

  toggleModuleStep(id: string): void {
    this.selectedModStepId = this.selectedModStepId === id ? null : id;
  }

  selectModuleStep(stepId: string, modStepId: string, ev: Event): void {
    ev.stopPropagation();
    this.selectStep(stepId);
    this.selectedModStepId = modStepId;
  }

  removeModuleStep(index: number, ev: Event): void {
    ev.stopPropagation();
    const id = this.selectedMod?.steps?.[index]?.id;
    this.withMod((mod) => {
      if (!mod.steps) return;
      mod.steps.splice(index, 1);
    });
    if (this.selectedModStepId === id) this.selectedModStepId = null;
  }

  patchModuleStepValue(id: string, key: string, value: any): void {
    this.withMod((mod) => {
      const step = mod.steps?.find((s) => s.id === id);
      if (!step) return;
      step.values = { ...step.values, [key]: value };
    });
  }

  toggleStepMulti(key: string, option: string): void {
    if (!this.selected) return;
    const cur = this.multiValues(this.selected.values, key);
    const next = cur.includes(option) ? cur.filter((d) => d !== option) : [...cur, option];
    this.patchStepValue(key, next);
  }

  toggleModuleMulti(id: string, key: string, option: string): void {
    this.withMod((mod) => {
      const step = mod.steps?.find((s) => s.id === id);
      if (!step) return;
      const cur = this.multiValues(step.values, key);
      const next = cur.includes(option) ? cur.filter((d) => d !== option) : [...cur, option];
      step.values = { ...step.values, [key]: next };
    });
  }

  multiValues(values: Record<string, any> | undefined, key: string): string[] {
    const v = values?.[key];
    if (Array.isArray(v)) return v.map((d) => String(d));
    return [];
  }

  hasMulti(values: Record<string, any> | undefined, key: string, option: string): boolean {
    return this.multiValues(values, key).includes(option);
  }

  /** Day chips for FRM Action. Module attempt days replace the defaults when set. */
  dayOptions(
    mod: S09OutreachMod | null,
    field: any,
    values?: Record<string, any>
  ): string[] {
    const fallback: string[] = field?.options?.length
      ? field.options
      : ["Day 1", "Day 3", "Day 5"];
    let options = fallback;
    if (field?.key === "days" && mod) {
      const seen = new Set<string>();
      const fromAttempts: string[] = [];
      for (const a of mod.attempts || []) {
        const raw = String(a.day || "").trim();
        if (!raw) continue;
        const label = /^day\b/i.test(raw) ? raw.replace(/^day\b/i, "Day") : `Day ${raw}`;
        if (seen.has(label)) continue;
        seen.add(label);
        fromAttempts.push(label);
      }
      if (fromAttempts.length) options = fromAttempts;
    }
    const selected = this.multiValues(values, field?.key);
    return [...options, ...selected.filter((d) => !options.includes(d))];
  }

  modStepSummary(ms: S09ModuleStep): string {
    const cat = catalogEntry(ms.kind);
    const parts: string[] = [];
    (cat?.fields || []).forEach((f: any) => {
      if (!this.fieldFilled(ms.values, f)) return;
      parts.push(this.fieldText(ms.values, f));
    });
    return parts.join(" · ") || "Not set";
  }

  private fieldFilled(values: Record<string, any> | undefined, field: any): boolean {
    const v = values?.[field.key];
    if (field.briefTpl) {
      if (!v || typeof v !== "object" || Array.isArray(v)) return false;
      return Object.keys(v).some((key) => String(v[key] || "").trim());
    }
    if (field.multi || Array.isArray(v)) return Array.isArray(v) && v.length > 0;
    return v != null && v !== "";
  }

  private fieldText(values: Record<string, any> | undefined, field: any): string {
    const v = values?.[field.key];
    if (field.briefTpl && v && typeof v === "object" && !Array.isArray(v)) {
      return Object.keys(v)
        .filter((key) => String(v[key] || "").trim())
        .map((key) => `${key}: ${this.templateName(String(v[key]))}`)
        .join(" · ");
    }
    if (Array.isArray(v)) return v.join(", ");
    return v == null ? "" : String(v);
  }

  private frmDecisionUnlinked(step: S09Step): boolean {
    if (step.kind !== "frmDecision") return false;
    return !this.branchTarget(step.id, "escalate") || !this.branchTarget(step.id, "wait");
  }

  private stepValuesIncomplete(kind: string, values: Record<string, any> | undefined): boolean {
    if (kind === "condition") {
      const v = values || {};
      if (!String(v["left"] || "").trim()) return true;
      if (!String(v["op"] || "").trim()) return true;
      if (!String(v["right"] || "").trim()) return true;
      return false;
    }
    if (kind === "assignCase") {
      const v = values || {};
      const team = String(v["team"] || "").trim();
      if (!team) return true;
      if (team === "Another leadership team" && !String(v["teamName"] || "").trim()) return true;
      return false;
    }
    if (kind === "frmDecision") {
      const v = values || {};
      if (!String(v["ask"] || "").trim()) return true;
      if (!String(v["unit"] || "").trim()) return true;
      const max = String(v["maxDays"] || "").trim();
      if (max && !/^[1-9]\d*$/.test(max)) return true;
      return false;
    }
    if (kind === "attempt") {
      const v = values || {};
      if (!String(v["name"] || "").trim()) return true;
      if (!String(v["runsAfter"] || "").trim()) return true;
      if (!String(v["template"] || "").trim()) return true;
      if (!this.mailList(v["to"]).length) return true;
      if (!v["onOutcome"]) return true;
      return false;
    }
    const cat = catalogEntry(kind);
    if (!cat?.fields?.length) return false;
    return cat.fields.some((f: any) => !f.optional && !this.fieldFilled(values, f));
  }

  findings(): S09Finding[] {
    const out: S09Finding[] = [];
    const steps = this.doc?.steps || [];
    if (!steps.length) {
      out.push({
        level: "INFO",
        where: "Canvas",
        message:
          "Only the trigger is placed. Add steps from the library to build the sequence.",
        colour: "#7a8499",
      });
    }
    const incomplete = steps.filter((n) => {
      if (n.kind === "outreachModule") {
        const m = n.mod || defaultMod();
        if (!m.attempts.length || m.attempts.some((a) => !a.day || !a.tpl)) return true;
        const stepsIncomplete = (m.steps || []).some((ms) =>
          this.stepValuesIncomplete(ms.kind, ms.values)
        );
        const afterIncomplete = (m.attempts || []).some((a) =>
          (a.after || []).some((ms) => this.stepValuesIncomplete(ms.kind, ms.values))
        );
        return stepsIncomplete || afterIncomplete;
      }
      return this.stepValuesIncomplete(String(n.kind), n.values) || this.frmDecisionUnlinked(n);
    });
    if (incomplete.length) {
      out.push({
        level: "ERROR",
        where: `${incomplete.length} step${incomplete.length === 1 ? "" : "s"}`,
        message:
          "Required fields are empty. Open a step in the inspector to complete them before activate.",
        colour: "#d92d20",
      });
    }
    const recordsEnd = steps.some(
      (n) =>
        n.kind === "end" ||
        (n.kind === "attempt" && n.values?.["onOutcome"] === "end")
    );
    if (steps.length && !recordsEnd) {
      out.push({
        level: "WARNING",
        where: "Sequence",
        message:
          "No End step. The workflow will fall through after the last step rather than recording an explicit outcome.",
        colour: "#d99a1e",
      });
    }
    const sends = steps.filter((n) => {
      if (catalogEntry(String(n.kind))?.gated) return true;
      return (
        (n.mod?.steps || []).some((ms) => catalogEntry(String(ms.kind))?.gated) ||
        (n.mod?.attempts || []).some((a) =>
          (a.after || []).some((ms) => catalogEntry(String(ms.kind))?.gated)
        )
      );
    });
    if (sends.length) {
      out.push({
        level: "PASS",
        where: `${sends.length} gated step${sends.length === 1 ? "" : "s"}`,
        message:
          "Every send carries the six-check gate chain. No step can be configured to bypass it.",
        colour: "#2e9b5f",
      });
    }
    if (!out.length) {
      out.push({
        level: "PASS",
        where: "Workflow",
        message: "No blocking issues. Review trigger settings, then activate.",
        colour: "#2e9b5f",
      });
    }
    return out;
  }

  get valBadge(): { label: string; ok: boolean } {
    const f = this.findings();
    if (f.some((x) => x.level === "ERROR"))
      return { label: "BLOCKED", ok: false };
    if (f.some((x) => x.level === "WARNING"))
      return { label: "REVIEW", ok: false };
    return { label: "READY", ok: true };
  }
}
